import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { CategoryProduct } from "@/lib/catalog";
import { conditionLabel, formatPrice } from "@/lib/catalog";
import { useChristmasTheme } from "@/hooks/useChristmasTheme";

interface BannerSlide {
  id: string;
  type: "banner";
  image: string;
  eyebrow: string;
  title: string;
  subtitle?: string;
  cta: string;
  ctaTo?: string;
  secondaryCta?: string;
  secondaryCtaTo?: string;
}

interface ProductSlide {
  id: string;
  type: "product";
  product: CategoryProduct;
}

type Slide = BannerSlide | ProductSlide;

const baseBannerSlides: BannerSlide[] = [
  {
    id: "tech",
    type: "banner",
    image:
      "https://readdy.ai/api/search-image?query=Modern%20technology%20sale%20banner%20background%20with%20floating%20smartphones%20laptops%20and%20headphones%20over%20a%20deep%20indigo%20gradient%2C%20soft%20glowing%20bokeh%20lights%2C%20sleek%20minimalist%20composition%2C%20premium%20ecommerce%20campaign%20aesthetic%2C%20high%20quality%20render&width=1200&height=600&seq=vendeya-banner-1&orientation=landscape",
    eyebrow: "Semana Tech",
    title: "Hasta 40% en electrónica",
    cta: "Ver ofertas",
    ctaTo: "/categoria/electronica",
  },
  {
    id: "fashion",
    type: "banner",
    image:
      "https://storage.helloreaddy.io/project_files/360884e8-4dc5-4075-b4f7-2a213dd9c223/d6912a0f-cf2b-4f59-8e2d-02b792317600_compressed_ChatGPT-Image-10-sept-2026-02_14_26-a.m..webp",
    eyebrow: "Moda de temporada",
    title: "Renueva tu clóset",
    cta: "Explorar moda",
    ctaTo: "/categoria/moda",
  },
  {
    id: "home",
    type: "banner",
    image:
      "https://readdy.ai/api/search-image?query=Cozy%20home%20and%20furniture%20banner%20background%20with%20a%20modern%20sofa%20and%20decorative%20plants%20over%20a%20soft%20sage%20green%20gradient%2C%20warm%20natural%20lighting%2C%20elegant%20minimalist%20composition%2C%20premium%20ecommerce%20campaign%20aesthetic%2C%20high%20detail&width=1200&height=600&seq=vendeya-banner-3&orientation=landscape",
    eyebrow: "Hogar",
    title: "Dale vida a tus espacios",
    cta: "Ver hogar",
    ctaTo: "/categoria/hogar",
  },
];

const christmasBannerSlides: BannerSlide[] = [
  {
    id: "xmas-hero",
    type: "banner",
    image:
      "https://readdy.ai/api/search-image?query=Elegant%20Christmas%20marketplace%20hero%20with%20a%20beautifully%20decorated%20Christmas%20tree%2C%20wrapped%20gift%20boxes%20in%20red%20green%20and%20gold%2C%20warm%20golden%20string%20lights%20and%20soft%20falling%20snow%2C%20deep%20emerald%20green%20and%20crimson%20red%20gradient%20backdrop%2C%20premium%20festive%20ecommerce%20campaign%20aesthetic%2C%20sophisticated%20modern%20composition%2C%20high%20detail%20clean%20and%20professional&width=1600&height=900&seq=vendeya-xmas-hero-1&orientation=landscape",
    eyebrow: "🎄 Temporada Navideña",
    title: "La Navidad se vive en VendeYa",
    subtitle: "Encuentra, vende y descubre todo lo que buscas esta Navidad.",
    cta: "Explorar productos",
    ctaTo: "/#categorias",
    secondaryCta: "Publicar producto",
    secondaryCtaTo: "/seller/dashboard",
  },
  {
    id: "xmas-tech",
    type: "banner",
    image:
      "https://readdy.ai/api/search-image?query=Modern%20Christmas%20technology%20gift%20banner%20with%20smartphones%20headphones%20and%20a%20smartwatch%20among%20festive%20gold%20and%20red%20decorations%2C%20soft%20golden%20bokeh%20lights%2C%20deep%20green%20gradient%20background%2C%20premium%20ecommerce%20campaign%20aesthetic%2C%20sleek%20minimalist%20composition%2C%20high%20detail&width=1200&height=600&seq=vendeya-xmas-banner-tech&orientation=landscape",
    eyebrow: "🎁 Regalos tech",
    title: "Sorprende con tecnología",
    cta: "Ver electrónica",
    ctaTo: "/categoria/electronica",
  },
  {
    id: "xmas-home",
    type: "banner",
    image:
      "https://readdy.ai/api/search-image?query=Cozy%20Christmas%20home%20interior%20banner%20with%20a%20modern%20sofa%20warm%20blankets%20and%20decorative%20plants%20accented%20with%20gold%20stars%20and%20pine%20branches%2C%20soft%20warm%20lighting%2C%20deep%20red%20and%20green%20gradient%2C%20premium%20festive%20ecommerce%20aesthetic%2C%20elegant%20minimalist%20composition%2C%20high%20detail&width=1200&height=600&seq=vendeya-xmas-banner-home&orientation=landscape",
    eyebrow: "✨ Hogar acogedor",
    title: "Dale calidez a tus espacios",
    cta: "Ver hogar",
    ctaTo: "/categoria/hogar",
  },
];

interface HeroCarouselProps {
  products?: CategoryProduct[];
}

export default function HeroCarousel({ products = [] }: HeroCarouselProps) {
  const { enabled, animations } = useChristmasTheme();
  const trackRef = useRef<HTMLDivElement>(null);
  const [, setActive] = useState(0);
  const activeRef = useRef(0);
  const programmatic = useRef(false);

  const banners = enabled ? christmasBannerSlides : baseBannerSlides;

  const slides: Slide[] = [
    ...banners,
    ...products.map((product) => ({
      id: `product-${product.id}`,
      type: "product" as const,
      product,
    })),
  ];

  // Ref para que el autoplay siempre use la cantidad de slides más reciente,
  // incluso cuando cargan los productos destacados después del primer render.
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  const scrollToIndex = (index: number) => {
    const el = trackRef.current;
    if (!el) return;
    programmatic.current = true;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
    activeRef.current = index;
    setActive(index);
    window.setTimeout(() => {
      programmatic.current = false;
    }, 450);
  };

  const handleScroll = () => {
    if (programmatic.current) return;
    const el = trackRef.current;
    if (!el) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    if (index !== activeRef.current) {
      activeRef.current = index;
      setActive(index);
    }
  };

  useEffect(() => {
    const id = window.setInterval(() => {
      const total = slidesRef.current.length;
      if (total === 0) return;
      const next = (activeRef.current + 1) % total;
      scrollToIndex(next);
    }, 5000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <section className="relative">
      <div
        ref={trackRef}
        onScroll={handleScroll}
        className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto"
      >
        {slides.map((slide) => {
          if (slide.type === "banner") {
            return (
              <div
                key={slide.id}
                className="relative h-[440px] w-full shrink-0 snap-center md:h-[560px]"
              >
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>

                {/* destellos decorativos en banners navideños */}
                {enabled && (
                  <>
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none absolute left-6 top-6 text-lg text-accent-300 ${
                        animations ? "xmas-twinkle" : ""
                      }`}
                    >
                      ✦
                    </span>
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none absolute right-10 top-14 text-sm text-accent-200 ${
                        animations ? "xmas-twinkle" : ""
                      }`}
                    >
                      ✦
                    </span>
                  </>
                )}

                <div className="absolute inset-x-0 bottom-0 px-5 pb-8 md:px-10 md:pb-12">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                    <i className="ri-sparkling-2-line text-accent-400"></i>
                    {slide.eyebrow}
                  </span>
                  <h2 className="mt-3 font-heading text-2xl font-bold text-white md:text-4xl">
                    {slide.title}
                  </h2>
                  {slide.subtitle && (
                    <p className="mt-2 max-w-md text-sm text-white/85 md:text-base">
                      {slide.subtitle}
                    </p>
                  )}
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Link
                      to={slide.ctaTo ?? "/#categorias"}
                      className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-transform active:scale-95 dark:text-foreground-50"
                    >
                      {slide.cta}
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                    {slide.secondaryCta && (
                      <Link
                        to={slide.secondaryCtaTo ?? "/"}
                        className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/50 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                      >
                        {slide.secondaryCta}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          const product = slide.product;
          const image = product.images?.[0];

          return (
            <div
              key={slide.id}
              data-product-shop
              className="relative h-[440px] w-full shrink-0 snap-center md:h-[560px]"
            >
              {image ? (
                <img
                  src={image}
                  alt={product.title}
                  className="h-full w-full object-cover object-top"
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center bg-background-100 text-foreground-300">
                  <i className="ri-image-line text-4xl"></i>
                </span>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
              <div className="absolute inset-x-0 bottom-0 px-5 pb-8 md:px-10 md:pb-12">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
                  <i className="ri-star-fill"></i>
                  Destacado
                </span>
                <h2 className="mt-3 font-heading text-2xl font-bold text-white md:text-4xl">
                  {product.title}
                </h2>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <span className="font-heading text-lg font-bold text-white md:text-xl">
                    {formatPrice(product.price, product.currency)}
                  </span>
                  <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-semibold text-white backdrop-blur-sm">
                    {conditionLabel(product.condition)}
                  </span>
                </div>
                <Link
                  to={`/producto/${product.id}`}
                  className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-transform active:scale-95 dark:text-foreground-50"
                >
                  Ver producto
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          );
        })}
      </div>

    </section>
  );
}